import { useState, useRef, useCallback } from 'react';

interface GalleryItem {
  id: string;
  treatment: string;
  shades: string;
  duration: string;
  beforeAlt: string;
  afterAlt: string;
  beforeSeq: number;
  afterSeq: number;
}

const galleryItems: GalleryItem[] = [
  {
    id: 'g1',
    treatment: 'Smile Premium',
    shades: 'sichtbar heller',
    duration: '60 Min.',
    beforeAlt: 'Zähne vor der kosmetischen Zahnaufhellung – natürliche Ausgangsfarbe',
    afterAlt: 'Zähne nach Smile Premium – strahlend weiß',
    beforeSeq: 301,
    afterSeq: 302,
  },
  {
    id: 'g2',
    treatment: 'Smile Classic',
    shades: 'sichtbar heller',
    duration: '45 Min.',
    beforeAlt: 'Zähne vor der Anwendung – leicht verfärbt durch Kaffee',
    afterAlt: 'Zähne nach Smile Classic – deutlich heller und strahlender',
    beforeSeq: 303,
    afterSeq: 304,
  },
  {
    id: 'g3',
    treatment: 'Smile Starter',
    shades: 'sichtbar heller',
    duration: '30 Min.',
    beforeAlt: 'Ausgangszustand vor der kosmetischen Zahnaufhellung Köln',
    afterAlt: 'Ergebnis nach Smile Starter – natürlich strahlend',
    beforeSeq: 305,
    afterSeq: 306,
  },
  {
    id: 'g4',
    treatment: 'Smile Premium',
    shades: 'sichtbar heller',
    duration: '60 Min.',
    beforeAlt: 'Verfärbte Zähne vor der kosmetischen Aufhellung',
    afterAlt: 'Maximales Ergebnis nach Smile Premium – strahlend weiß',
    beforeSeq: 307,
    afterSeq: 308,
  },
];

interface SliderCardProps {
  item: GalleryItem;
}

function SliderCard({ item }: SliderCardProps) {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateSlider = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    updateSlider(e.clientX);
  }, [isDragging, updateSlider]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    updateSlider(e.touches[0].clientX);
  }, [updateSlider]);

  return (
    <div className="flex flex-col gap-0 rounded-2xl overflow-hidden ring-1 ring-charcoal/10">
      {/* Slider image area */}
      <div
        ref={containerRef}
        className="relative select-none overflow-hidden cursor-col-resize"
        style={{ height: '280px' }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* AFTER image (full width, behind) */}
        <div className="absolute inset-0">
          <img
            src={`https://public.readdy.ai/ai/img_res/d3500cdc0ce4f5ab2f5d035418393f69.jpg${item.afterSeq}&orientation=portrait`}
            alt={item.afterAlt}
            className="w-full h-full object-cover object-top"
            draggable={false}
          />
          {/* After label */}
          <div className="absolute bottom-3 right-3">
            <span className="font-sans text-xs font-semibold text-white bg-rose px-2.5 py-1 rounded-full uppercase tracking-wide">
              Nachher
            </span>
          </div>
        </div>

        {/* BEFORE image (clipped to left of slider) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${sliderPos}%` }}
        >
          <div className="absolute inset-0" style={{ width: `${100 / (sliderPos / 100)}%`, maxWidth: 'none' }}>
            <img
              src={`https://readdy.ai/api/search-image?query=close%20up%20of%20natural%20teeth%20before%20professional%20whitening%20treatment%2C%20slightly%20yellowed%20or%20stained%20teeth%2C%20realistic%20natural%20tooth%20color%2C%20soft%20warm%20studio%20lighting%2C%20clean%20minimal%20background%2C%20beauty%20photography%2C%20cosmetic%20dentistry%20before%20photo%2C%20feminine%20elegant%2C%20high%20end%20studio%20Cologne%20Germany&width=600&height=560&seq=${item.beforeSeq}&orientation=portrait`}
              alt={item.beforeAlt}
              className="w-full h-full object-cover object-top"
              draggable={false}
            />
          </div>
          {/* Before label */}
          <div className="absolute bottom-3 left-3">
            <span className="font-sans text-xs font-semibold text-white bg-charcoal/80 px-2.5 py-1 rounded-full uppercase tracking-wide">
              Vorher
            </span>
          </div>
        </div>

        {/* Divider line */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white z-10 pointer-events-none"
          style={{ left: `${sliderPos}%` }}
        />

        {/* Drag handle */}
        <div
          className="absolute top-1/2 z-20 -translate-y-1/2 -translate-x-1/2 w-9 h-9 flex items-center justify-center bg-white rounded-full cursor-col-resize"
          style={{ left: `${sliderPos}%`, boxShadow: '0 2px 12px rgba(0,0,0,0.18)' }}
          onMouseDown={handleMouseDown}
          onTouchStart={() => setIsDragging(true)}
          onTouchMove={handleTouchMove}
          onTouchEnd={() => setIsDragging(false)}
        >
          <i className="ri-arrow-left-right-line text-charcoal text-sm" />
        </div>
      </div>

      {/* Info bar */}
      <div className="bg-white px-5 py-4 flex items-center justify-between gap-3">
        <div>
          <p className="font-sans text-sm font-semibold text-charcoal">{item.treatment}</p>
          <p className="font-sans text-xs text-charcoal/50">{item.duration} Anwendung</p>
        </div>
        <div className="flex items-center gap-1.5 bg-rose/10 px-3 py-1.5 rounded-full">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-sun-line text-rose text-xs" />
          </span>
          <span className="font-sans text-xs font-semibold text-rose">{item.shades}</span>
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfterGallery() {
  return (
    <section className="bg-offwhite py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">
            Echte Ergebnisse
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-charcoal mt-3 mb-4">
            Vorher &amp; Nachher
          </h2>
          <p className="font-sans text-sm text-charcoal/55 max-w-md mx-auto leading-relaxed">
            Ziehe den Regler, um den Unterschied zu sehen — echte Ergebnisse unserer Kund*innen aus Köln.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-drag-move-line text-charcoal/40 text-base" />
            </span>
            <span className="font-sans text-xs text-charcoal/40">Regler ziehen zum Vergleichen</span>
          </div>
        </div>

        {/* Gallery grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {galleryItems.map((item) => (
            <SliderCard key={item.id} item={item} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="font-sans text-sm text-charcoal/55 mb-5">
            Bereit für dein eigenes Vorher-Nachher-Ergebnis?
          </p>
          <a
            href="https://simplybook.me"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-sm px-7 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line text-sm" />
            </span>
            Jetzt Termin buchen
          </a>
        </div>
      </div>
    </section>
  );
}
